import React, { useState, useEffect } from "react";
import Accordion, {
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../ui/Accordion";
import LoadingSpinner from "../ui/LoadingSpinner";
import { ComponentErrorBoundary } from "../ErrorBoundary";
import {
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon,
  MapPinIcon,
  ChartBarIcon,
  LinkIcon,
  PlusIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

/**
 * LinkedRecordsSection Component
 *
 * Displays related records (Projects, Valuations, etc.) as expandable accordions
 * within forms. Supports Deal→Project and Project→Valuation relationships.
 */
export default function LinkedRecordsSection({
  recordType, // 'project' | 'valuation'
  linkedRecordId, // ID of the linked record
  className = "",
  onNavigate, // Callback for navigation to full record view
  onEdit, // Callback for inline editing
  onAdd, // Callback for adding new linked record
  onUnlink, // Callback for unlinking records
  ...props
}) {
  const [linkedRecord, setLinkedRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load linked record data
  useEffect(() => {
    if (!linkedRecordId) return;

    const loadLinkedRecord = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/${recordType}s.json`);
        if (!response.ok) throw new Error(`Failed to load ${recordType}s`);

        const data = await response.json();
        const records = data[`${recordType}s`] || [];
        const record = records.find((r) => r.id === linkedRecordId);

        if (!record) {
          throw new Error(`${recordType} with ID ${linkedRecordId} not found`);
        }

        setLinkedRecord(record);
      } catch (err) {
        console.error(`Error loading linked ${recordType}:`, err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadLinkedRecord();
  }, [linkedRecordId, recordType]);

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <LoadingSpinner size="sm" message={`Loading ${recordType}...`} />
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600 text-sm">Error: {error}</p>
      </div>
    );
  }

  // Render empty state when no linked record
  if (!linkedRecord) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <LinkIcon className="h-5 w-5 text-gray-400" />
            <span className="text-gray-600">No {recordType} linked</span>
          </div>
          {onAdd && (
            <button
              onClick={() => onAdd(recordType)}
              className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add {recordType}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <ComponentErrorBoundary componentName="LinkedRecordsSection">
      <div className={`linked-records-section ${className}`} {...props}>
        <Accordion type="single" collapsible defaultValue={null}>
          <AccordionItem
            value={linkedRecord.id}
            className="border border-gray-200 rounded-lg"
          >
            <AccordionTrigger className="px-4 py-3 hover:bg-gray-50">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-3">
                  {recordType === "project" ? (
                    <BuildingOfficeIcon className="h-5 w-5 text-blue-500" />
                  ) : (
                    <CurrencyDollarIcon className="h-5 w-5 text-green-500" />
                  )}
                  <div className="text-left">
                    <h4 className="font-medium text-gray-900">
                      {linkedRecord.name}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {getRecordSubtitle(linkedRecord, recordType)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <StatusBadge record={linkedRecord} recordType={recordType} />
                </div>
              </div>
            </AccordionTrigger>

            <AccordionContent className="px-4 pb-4">
              <div className="border-t border-gray-100 pt-4">
                {recordType === "project" ? (
                  <ProjectSummary project={linkedRecord} />
                ) : (
                  <ValuationSummary valuation={linkedRecord} />
                )}

                {/* Action buttons */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    {onNavigate && (
                      <button
                        onClick={() => onNavigate(linkedRecord.id, recordType)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1" />
                        View Details
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={() => onEdit(linkedRecord.id, recordType)}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {onUnlink && (
                    <button
                      onClick={() => onUnlink(linkedRecord.id, recordType)}
                      className="inline-flex items-center px-3 py-1 border border-red-300 rounded-md text-sm text-red-700 bg-white hover:bg-red-50"
                    >
                      Unlink
                    </button>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </ComponentErrorBoundary>
  );
}

/**
 * Get record subtitle for display
 */
function getRecordSubtitle(record, recordType) {
  if (recordType === "project") {
    return `${record.type} • ${record.status} • ${record.location?.address || "No address"}`;
  } else if (recordType === "valuation") {
    return `${record.type} • ${record.status} • ${formatCurrency(record.valuation?.marketValue || record.valuation?.gdv)}`;
  }
  return record.description || "";
}

/**
 * Status badge component
 */
function StatusBadge({ record, recordType }) {
  const getStatusColor = (status) => {
    const colors = {
      active: "bg-green-100 text-green-800",
      completed: "bg-blue-100 text-blue-800",
      approved: "bg-green-100 text-green-800",
      pending: "bg-yellow-100 text-yellow-800",
      planning: "bg-orange-100 text-orange-800",
      "under-review": "bg-yellow-100 text-yellow-800",
      "due-diligence": "bg-purple-100 text-purple-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}
    >
      {record.status}
    </span>
  );
}

/**
 * Project summary component
 */
function ProjectSummary({ project }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <MapPinIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Location</span>
          </div>
          <p className="text-sm font-medium">
            {project.location?.address || "No address"}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <UserIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Project Manager</span>
          </div>
          <p className="text-sm font-medium">
            {project.contacts?.projectManager || "Not assigned"}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Timeline</span>
          </div>
          <p className="text-sm font-medium">
            {project.timeline?.startDate} -{" "}
            {project.timeline?.expectedCompletion}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <ChartBarIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Financial</span>
          </div>
          <p className="text-sm font-medium">
            GDV: {formatCurrency(project.financial?.totalGDV)} | Profit:{" "}
            {formatCurrency(project.financial?.profit)}
          </p>
        </div>
      </div>

      {project.description && (
        <div className="pt-2 border-t border-gray-100">
          <p className="text-sm text-gray-600">{project.description}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Valuation summary component
 */
function ValuationSummary({ valuation }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Market Value</span>
          </div>
          <p className="text-sm font-medium">
            {formatCurrency(
              valuation.valuation?.marketValue || valuation.valuation?.gdv
            )}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <UserIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Valuer</span>
          </div>
          <p className="text-sm font-medium">
            {valuation.valuer?.firm || "Not specified"}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Valuation Date</span>
          </div>
          <p className="text-sm font-medium">
            {valuation.dates?.reported || "Not specified"}
          </p>
        </div>

        <div className="space-y-2">
          <span className="text-sm text-gray-600">Method</span>
          <p className="text-sm font-medium">
            {valuation.methodology || "Not specified"}
          </p>
        </div>
      </div>

      {valuation.description && (
        <div className="pt-2 border-t border-gray-100">
          <p className="text-sm text-gray-600">{valuation.description}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Currency formatting utility
 */
function formatCurrency(amount) {
  if (!amount || isNaN(amount)) return "N/A";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}
